// Trust Events Components
import AjaxUtils from "../../util/ajax_utils";
import { ApckColumn } from '../../main';

/**
 * Mapping from event_type identifiers to human-readable labels, icons and
 * colour classes used throughout the events list and sidebar.
 */
var EVENT_TYPE_META = {
    'trust_country_alert': {
        label: 'Country Alert',
        icon: 'fa-regular fa-flag',
        colour: 'medium',
    },
    'trust_risk_alert': {
        label: 'Risk Alert',
        icon: 'fa-regular fa-triangle-exclamation',
        colour: 'high',
    },
    'trust_new_vulnerability': {
        label: 'New Vulnerabilities',
        icon: 'fa-regular fa-shield-exclamation',
        colour: 'critical',
    },
    'trust_new_thirdparty': {
        label: 'New Third Parties',
        icon: 'fa-regular fa-globe',
        colour: 'medium',
    },
    'trust_geolocation_change': {
        label: 'Geolocation Change',
        icon: 'fa-regular fa-map-location',
        colour: 'medium',
    },
    'trust_new_geolocation': {
        label: 'New Geolocation',
        icon: 'fa-regular fa-map-location',
        colour: 'medium',
    },
    'trust_domain_expiration': {
        label: 'Domain Expiration',
        icon: 'fa-regular fa-calendar-exclamation',
        colour: 'high',
    },
    'trust_recently_registered': {
        label: 'Recently Registered',
        icon: 'fa-regular fa-calendar-plus',
        colour: 'medium',
    },
};

function formatDate(isoString) {
    if (!isoString) return '—';
    var d = new Date(isoString);
    var day = ('0' + d.getDate()).slice(-2);
    var month = ('0' + (d.getMonth() + 1)).slice(-2);
    var year = d.getFullYear();
    var hours = ('0' + d.getHours()).slice(-2);
    var mins = ('0' + d.getMinutes()).slice(-2);
    return day + '/' + month + '/' + year + ' ' + hours + ':' + mins;
}

function timeAgo(isoString) {
    if (!isoString) return '';
    var now = new Date();
    var then = new Date(isoString);
    var diff = Math.floor((now - then) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return Math.floor(diff / 86400) + 'd ago';
}

function getEventMeta(eventType) {
    return EVENT_TYPE_META[eventType] || {
        label: eventType.replace(/_/g, ' '),
        icon: 'fa-regular fa-bell',
        colour: 'medium',
    };
}


// -----------------------------------------------------------------------
// Event detail panel — displayed in the right-side modal
// -----------------------------------------------------------------------
var ApckEventDetailPanel = {
    props: {
        event: { type: Object, required: true },
    },
    data: function () {
        return {
            activeTabId: 'summary'
        };
    },
    computed: {
        meta: function () { return getEventMeta(this.event.event_type); },
        data: function () { return this.event.event_data || {}; },
        tabs: function () {
            var t = [
                { id: 'summary', title: 'Summary' }
            ];
            var d = this.data;
            var et = this.event.event_type;
            if (d.vulnerabilities && d.vulnerabilities.length) {
                t.push({ id: 'vulnerabilities', title: 'Vulnerabilities (' + d.vulnerabilities.length + ')' });
            }
            if (d.thirdparties && d.thirdparties.length && et !== 'trust_domain_expiration' && et !== 'trust_recently_registered') {
                t.push({ id: 'thirdparties', title: 'Third Parties (' + d.thirdparties.length + ')' });
            }
            if (d.changes && d.changes.length) {
                t.push({ id: 'changes', title: 'Geo Changes (' + d.changes.length + ')' });
            }
            if (d.thirdparties && d.thirdparties.length && et === 'trust_domain_expiration') {
                t.push({ id: 'expiring', title: 'Expiring (' + d.thirdparties.length + ')' });
            }
            if (d.thirdparties && d.thirdparties.length && et === 'trust_recently_registered') {
                t.push({ id: 'registered', title: 'Recently Registered (' + d.thirdparties.length + ')' });
            }
            t.push({ id: 'delivery', title: 'Delivery' });
            return t;
        },
        eventLink: function () {
            var ev = this.event;
            var d = ev.event_data || {};
            var params = [];
            // Use processing timestamps as date range filter
            if (d.processing_started_at) {
                params.push('date_from=' + encodeURIComponent(d.processing_started_at));
            }
            if (d.processing_completed_at) {
                params.push('date_to=' + encodeURIComponent(d.processing_completed_at));
            }
            var qs;
            switch (ev.event_type) {
                case 'trust_new_vulnerability':
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/vulnerabilities' + qs;
                case 'trust_country_alert':
                    if (d.country_code) params.push('country=' + encodeURIComponent(d.country_code));
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/relationships' + qs;
                case 'trust_risk_alert':
                    if (d.risk_level) params.push('risk_level=' + encodeURIComponent(d.risk_level));
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/relationships' + qs;
                case 'trust_new_geolocation':
                    if (d.country_code) params.push('country=' + encodeURIComponent(d.country_code));
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/countries' + qs;
                case 'trust_geolocation_change':
                case 'trust_new_thirdparty':
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/relationships' + qs;
                case 'trust_domain_expiration':
                case 'trust_recently_registered':
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/relationships' + qs;
                default:
                    return null;
            }
        },
        eventLinkLabel: function () {
            switch (this.event.event_type) {
                case 'trust_new_vulnerability': return 'View Vulnerabilities';
                case 'trust_new_thirdparty': return 'View Relationships';
                case 'trust_country_alert': return 'View Relationships';
                case 'trust_risk_alert': return 'View Relationships';
                case 'trust_geolocation_change': return 'View Relationships';
                case 'trust_new_geolocation': return 'View Countries';
                case 'trust_domain_expiration': return 'View Relationships';
                case 'trust_recently_registered': return 'View Relationships';
                default: return 'View';
            }
        },
    },
    methods: {
        formatDate: formatDate,
        timeAgo: timeAgo,
        switchTab: function (tab) {
            this.activeTabId = tab.id;
        },
        thirdpartyUrl: function (thirdpartyId) {
            if (!thirdpartyId) return null;
            return '/trust/thirdparty/' + encodeURIComponent(thirdpartyId) + '/view';
        },
        domainUrl: function (domainId) {
            if (!domainId) return null;
            return '/trust/domain/' + encodeURIComponent(domainId) + '/view';
        },
    },
    template: `
        <div class="event-detail-panel">
            <div class="event-detail-panel__header">
                <div class="event-detail-panel__header-icon" :class="'color-' + meta.colour">
                    <i :class="meta.icon"></i>
                </div>
                <div class="event-detail-panel__header-info">
                    <h3>{{ meta.label }}</h3>
                    <span class="event-detail-panel__date">
                        <i class="fa-regular fa-clock"></i>
                        {{ formatDate(event.detected_at) }}
                        <em>({{ timeAgo(event.detected_at) }})</em>
                    </span>
                </div>
            </div>

            <div v-if="eventLink" class="event-detail-panel__link">
                <a :href="eventLink">
                    <i class="fa-regular fa-arrow-right"></i> {{ eventLinkLabel }}
                </a>
            </div>

            <!-- Tabs header -->
            <ui-layout direction="row" gap="0" align="end">
                <ui-button
                    v-for="tab in tabs"
                    :key="tab.id"
                    :label="tab.title"
                    :type="activeTabId === tab.id ? 'primary' : 'outline'"
                    tab
                    @click.prevent="switchTab(tab)"
                />
            </ui-layout>

            <!-- Tabs content -->
            <ui-panel-base
                direction="column"
                justify="start"
                align="stretch"
                gap="2"
                class="panel-box--tab-content"
            >
                <!-- Summary tab -->
                <ui-layout v-if="activeTabId === 'summary'" direction="column" gap="4">
                    <ui-panel-base direction="column" gap="1">
                        <div v-if="data.vulnerability_count" class="event-detail-panel__stat">
                            <strong>{{ data.vulnerability_count }}</strong> new vulnerabilities across
                            <strong>{{ data.affected_thirdparty_count || 0 }}</strong> third parties
                        </div>
                        <div v-if="data.country_name" class="event-detail-panel__stat">
                            <strong>{{ data.relationship_count }}</strong> relationships with
                            <strong>{{ data.country_name }}</strong> ({{ data.country_code }})
                        </div>
                        <div v-if="data.risk_name" class="event-detail-panel__stat">
                            <strong>{{ data.relationship_count }}</strong> <strong>{{ data.risk_name }}</strong> level relationships
                        </div>
                        <div v-if="data.thirdparty_count && event.event_type !== 'trust_domain_expiration' && event.event_type !== 'trust_recently_registered'" class="event-detail-panel__stat">
                            <strong>{{ data.thirdparty_count }}</strong> new third parties detected
                        </div>
                        <div v-if="data.change_count" class="event-detail-panel__stat">
                            <strong>{{ data.change_count }}</strong> geolocation changes detected
                        </div>
                        <div v-if="data.geolocation_count" class="event-detail-panel__stat">
                            <strong>{{ data.geolocation_count }}</strong> third parties in new geolocation <strong>{{ data.country_name || data.country_code }}</strong>
                        </div>
                        <div v-if="data.thirdparty_count && event.event_type === 'trust_domain_expiration'" class="event-detail-panel__stat">
                            <strong>{{ data.thirdparty_count }}</strong> third parties with domains expiring soon
                        </div>
                        <div v-if="data.thirdparty_count && event.event_type === 'trust_recently_registered'" class="event-detail-panel__stat">
                            <strong>{{ data.thirdparty_count }}</strong> third parties with recently registered domains
                        </div>
                    </ui-panel-base>
                </ui-layout>

                <!-- Vulnerabilities tab -->
                <ui-layout v-if="activeTabId === 'vulnerabilities'" direction="column" gap="2">
                    <div class="vue-ui-datatable">
                        <table>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th style="width: 100px;">Severity</th>
                                    <th>Third Party</th>
                                    <th>Target URL</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(v, i) in data.vulnerabilities" :key="i">
                                    <td><div class="cell limited-text"><span class="cell-value">{{ v.name }}</span></div></td>
                                    <td><div class="cell"><span :class="'severity-' + (v.severity || 'unknown')">{{ v.severity || '-' }}</span></div></td>
                                    <td><div class="cell limited-text"><span class="cell-value">
                                        <a v-if="v.thirdparty_id" :href="thirdpartyUrl(v.thirdparty_id)" class="cell-link">{{ v.hostname }}</a>
                                        <span v-else>{{ v.hostname }}</span>
                                    </span></div></td>
                                    <td><div class="cell limited-text"><span class="cell-value">{{ v.target_url }}</span></div></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ui-layout>

                <!-- Third Parties tab -->
                <ui-layout v-if="activeTabId === 'thirdparties'" direction="column" gap="2">
                    <div class="vue-ui-datatable">
                        <table>
                            <thead>
                                <tr>
                                    <th>Hostname</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(t, i) in data.thirdparties" :key="i">
                                    <td><div class="cell limited-text"><span class="cell-value">
                                        <a v-if="t.thirdparty_id" :href="thirdpartyUrl(t.thirdparty_id)" class="cell-link">{{ t.hostname }}</a>
                                        <span v-else>{{ t.hostname || t }}</span>
                                    </span></div></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ui-layout>

                <!-- Geolocation Changes tab -->
                <ui-layout v-if="activeTabId === 'changes'" direction="column" gap="2">
                    <div class="vue-ui-datatable">
                        <table>
                            <thead>
                                <tr>
                                    <th>Hostname</th>
                                    <th style="width: 120px;">From</th>
                                    <th style="width: 120px;">To</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(c, i) in data.changes" :key="i">
                                    <td><div class="cell limited-text"><span class="cell-value">
                                        <a v-if="c.thirdparty_id" :href="thirdpartyUrl(c.thirdparty_id)" class="cell-link">{{ c.hostname }}</a>
                                        <span v-else>{{ c.hostname }}</span>
                                    </span></div></td>
                                    <td><div class="cell"><span class="cell-value">{{ c.old_country }}</span></div></td>
                                    <td><div class="cell"><span class="cell-value">{{ c.new_country }}</span></div></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ui-layout>

                <!-- Expiring Third Parties tab -->
                <ui-layout v-if="activeTabId === 'expiring'" direction="column" gap="2">
                    <div class="vue-ui-datatable">
                        <table>
                            <thead>
                                <tr>
                                    <th>Hostname</th>
                                    <th style="width: 120px;">Expires</th>
                                    <th style="width: 100px;">Days Left</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(d, i) in data.thirdparties" :key="i">
                                    <td><div class="cell limited-text"><span class="cell-value">
                                        <a v-if="d.thirdparty_id" :href="thirdpartyUrl(d.thirdparty_id)" class="cell-link">{{ d.hostname }}</a>
                                        <span v-else>{{ d.hostname }}</span>
                                    </span></div></td>
                                    <td><div class="cell"><span class="cell-value">{{ d.expiration_date }}</span></div></td>
                                    <td><div class="cell"><span class="cell-value" :class="d.days_until_expiry <= 7 ? 'text-danger' : ''">{{ d.days_until_expiry }}</span></div></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ui-layout>

                <!-- Recently Registered tab -->
                <ui-layout v-if="activeTabId === 'registered'" direction="column" gap="2">
                    <div class="vue-ui-datatable">
                        <table>
                            <thead>
                                <tr>
                                    <th>Hostname</th>
                                    <th style="width: 120px;">Registered</th>
                                    <th style="width: 100px;">Days Ago</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(d, i) in data.thirdparties" :key="i">
                                    <td><div class="cell limited-text"><span class="cell-value">
                                        <a v-if="d.thirdparty_id" :href="thirdpartyUrl(d.thirdparty_id)" class="cell-link">{{ d.hostname }}</a>
                                        <span v-else>{{ d.hostname }}</span>
                                    </span></div></td>
                                    <td><div class="cell"><span class="cell-value">{{ d.registration_date }}</span></div></td>
                                    <td><div class="cell"><span class="cell-value">{{ d.days_since_registration }}</span></div></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ui-layout>

                <!-- Delivery tab -->
                <ui-layout v-if="activeTabId === 'delivery'" direction="column" gap="2">
                    <ui-panel-base direction="column" gap="1">
                        <div class="event-detail-panel__delivery-row">
                            <span class="event-detail-panel__delivery-label">Email</span>
                            <span :class="event.delivered_email ? 'badge-delivered' : 'badge-pending'">
                                {{ event.delivered_email ? 'Sent' : 'Pending' }}
                            </span>
                        </div>
                        <div class="event-detail-panel__delivery-row">
                            <span class="event-detail-panel__delivery-label">Webhook</span>
                            <span :class="event.delivered_webhook ? 'badge-delivered' : 'badge-pending'">
                                {{ event.delivered_webhook ? 'Sent' : 'Pending' }}
                            </span>
                        </div>
                        <div v-if="event.dismissed" class="event-detail-panel__delivery-row">
                            <span class="event-detail-panel__delivery-label">Dismissed</span>
                            <span class="badge-dismissed">{{ formatDate(event.dismissed_at) }} by {{ event.dismissed_by }}</span>
                        </div>
                    </ui-panel-base>
                </ui-layout>
            </ui-panel-base>
        </div>
    `,
};


// -----------------------------------------------------------------------
// Custom column components for apck-table
// -----------------------------------------------------------------------
var EventTypeColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        meta: function () {
            return getEventMeta(this.record.event_type);
        },
        colourClass: function () {
            return 'color-' + this.meta.colour;
        },
    },
    template: `
        <apck-col-wrapper>
            <span class="event-type-cell" :class="colourClass">
                <i :class="meta.icon"></i>
                {{ meta.label }}
            </span>
        </apck-col-wrapper>
    `,
});

var EventSummaryColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        summary: function () {
            var d = this.record.event_data || {};
            var t = this.record.event_type;
            switch (t) {
                case 'trust_country_alert':
                    return (d.relationship_count || 0) + ' relationships with ' + (d.country_name || d.country_code || 'flagged country');
                case 'trust_risk_alert':
                    return (d.relationship_count || 0) + ' ' + (d.risk_name || '') + ' level relationships';
                case 'trust_new_vulnerability':
                    return (d.vulnerability_count || 0) + ' new vulnerabilities across ' + (d.affected_thirdparty_count || 0) + ' third parties';
                case 'trust_new_thirdparty':
                    return (d.thirdparty_count || 0) + ' new third parties detected';
                case 'trust_geolocation_change':
                    return (d.change_count || 0) + ' geolocation changes detected';
                case 'trust_new_geolocation':
                    return 'New geolocation: ' + (d.country_name || d.country_code || 'unknown') + ' (' + (d.geolocation_count || 0) + ' third parties)';
                case 'trust_domain_expiration':
                    return (d.thirdparty_count || 0) + ' third parties with expiring domains';
                case 'trust_recently_registered':
                    return (d.thirdparty_count || 0) + ' third parties with recently registered domains';
                default:
                    return t.replace(/_/g, ' ');
            }
        },
    },
    template: `
        <apck-col-wrapper>
            <span class="event-summary-cell">{{ summary }}</span>
        </apck-col-wrapper>
    `,
});

var EventStatusColumn = /*#__PURE__*/ ApckColumn.extend({
    computed: {
        emailSent: function () { return this.record.delivered_email; },
        webhookSent: function () { return this.record.delivered_webhook; },
        dismissed: function () { return this.record.dismissed; },
    },
    template: `
        <apck-col-wrapper>
            <span class="event-status-cell">
                <span v-if="emailSent" class="status-badge status-email" title="Email sent"><i class="fa-regular fa-envelope"></i></span>
                <span v-if="webhookSent" class="status-badge status-webhook" title="Webhook sent"><i class="fa-regular fa-globe"></i></span>
                <span v-if="dismissed" class="status-badge status-dismissed" title="Dismissed"><i class="fa-regular fa-check"></i></span>
            </span>
        </apck-col-wrapper>
    `,
});


// -----------------------------------------------------------------------
// Main events page component — table + right panel modal
// -----------------------------------------------------------------------
export var ApckTrustEvents = {
    components: {
        'apck-event-detail-panel': ApckEventDetailPanel,
    },
    props: {
        dataSrc: {
            type: String,
            default: '/trust/events/list',
        },
        debugEnabled: {
            type: Boolean,
            default: false,
        },
    },
    data: function () {
        return {
            loading: false,
            detailsModal: {
                render: false,
                open: false,
                event: null,
            },
            filters: {
                event_type: null,
                include_dismissed: null,
                date_from: null,
                date_to: null,
            },
            cols: [
                { data: 'event_type', heading: 'Type', ctype: 'event-type-column', width: 200 },
                { data: 'event_data', heading: 'Summary', ctype: 'event-summary-column' },
                { data: 'detected_at', heading: 'Detected', ctype: 'datecolumn', width: 160 },
                { data: 'delivered_email', heading: 'Status', ctype: 'event-status-column', width: 120, center: true },
            ],
            actions: [{
                title: 'View Details',
                icon: 'fa-eye',
                class: 'btn-secondary',
                handler: this.viewDetails,
                useOwnCol: true,
                hideLabel: true,
            }],
        };
    },
    computed: {
        tableSrc: function () {
            var params = [];
            if (this.filters.include_dismissed) params.push('include_dismissed=true');
            if (this.filters.event_type) params.push('event_type=' + encodeURIComponent(this.filters.event_type));
            if (this.filters.date_from) params.push('date_from=' + encodeURIComponent(this.filters.date_from));
            if (this.filters.date_to) params.push('date_to=' + encodeURIComponent(this.filters.date_to));
            return this.dataSrc + (params.length ? '?' + params.join('&') : '');
        },
        eventTypeOptions: function () {
            var opts = [{ value: null, label: 'All Event Types' }];
            for (var key in EVENT_TYPE_META) {
                opts.push({ value: key, label: EVENT_TYPE_META[key].label });
            }
            return opts;
        },
        typeFilterLabel: function () {
            if (!this.filters.event_type) return 'All Event Types';
            var meta = EVENT_TYPE_META[this.filters.event_type];
            return meta ? meta.label : this.filters.event_type;
        },
        tableKey: function () {
            return 'events-table-' + this._uid;
        },
        hasFilters: function () {
            return this.filters.event_type || this.filters.include_dismissed || this.filters.date_from || this.filters.date_to;
        },
    },
    created: function () {
        var Vue = this.$options._base || this.$root.constructor;
        Vue.component('apck-event-type-column', EventTypeColumn);
        Vue.component('apck-event-status-column', EventStatusColumn);
        Vue.component('apck-event-summary-column', EventSummaryColumn);
    },
    methods: {
        viewDetails: function (rec) {
            var cmp = this;
            var eventData = {
                id: rec.get('id'),
                event_type: rec.get('event_type'),
                event_data: rec.get('event_data'),
                hostname: rec.get('hostname'),
                detected_at: rec.get('detected_at'),
                delivered_email: rec.get('delivered_email'),
                delivered_webhook: rec.get('delivered_webhook'),
                delivered_at: rec.get('delivered_at'),
                dismissed: rec.get('dismissed'),
                dismissed_at: rec.get('dismissed_at'),
                dismissed_by: rec.get('dismissed_by'),
            };
            cmp.detailsModal.event = eventData;
            cmp.detailsModal.render = true;
            cmp.$nextTick(function () {
                cmp.detailsModal.open = true;
            });
        },
        closeDetails: function () {
            var cmp = this;
            cmp.detailsModal.open = false;
            setTimeout(function () {
                cmp.detailsModal.render = false;
                cmp.detailsModal.event = null;
            }, 400);
        },
        dismissEvent: function () {
            var cmp = this;
            var ev = cmp.detailsModal.event;
            if (!ev) return;
            AjaxUtils.ajax_post({
                url: '/trust/events/dismiss',
                data: JSON.stringify({ event_id: ev.id }),
                success: function () {
                    cmp.closeDetails();
                    if (cmp.$refs.eventsTable) {
                        cmp.$refs.eventsTable.load();
                    }
                },
                fail: function () {},
                opts: { no_response_message: 'Error dismissing event' }
            });
        },
        debugSendEmail: function () {
            var ev = this.detailsModal.event;
            if (!ev) return;
            AjaxUtils.ajax_post({
                url: '/trust/events/' + ev.id + '/debug/send-email',
                data: JSON.stringify({}),
                success: function () {},
                fail: function () {},
                opts: { no_response_message: 'Error sending debug email' }
            });
        },
        debugSendWebhook: function () {
            var ev = this.detailsModal.event;
            if (!ev) return;
            AjaxUtils.ajax_post({
                url: '/trust/events/' + ev.id + '/debug/send-webhook',
                data: JSON.stringify({}),
                success: function () {},
                fail: function () {},
                opts: { no_response_message: 'Error sending debug webhook' }
            });
        },
        setTypeFilter: function (val) {
            this.filters.event_type = val;
        },
        setDismissedFilter: function (val) {
            this.filters.include_dismissed = val;
        },
        handleSubmit: function () {
            if (this.$refs.eventsTable) {
                this.$refs.eventsTable.load(this.tableSrc);
            }
        },
        clearFilters: function () {
            this.filters.event_type = null;
            this.filters.include_dismissed = null;
            this.filters.date_from = null;
            this.filters.date_to = null;
            this.handleSubmit();
        },
        getEventMeta: getEventMeta,
        formatDate: formatDate,
        timeAgo: timeAgo,
    },
    template: `
        <ui-page title="Trust Events" stickyTitle>
            <template #title-bar-buttons>
            </template>
            <template #content>
                <ui-layout direction="column">
                    <ui-layout direction="column" justify="start">
                        <ui-layout gap="2" align="center">
                            <ui-icon type="secondary" icon="fa-filter" />
                            <ui-heading tag="span" size="md">Filters</ui-heading>
                        </ui-layout>

                        <ui-layout
                            tag="form"
                            gap="4"
                            align="end"
                            name="trust_events_filter"
                            @keydown.enter.native.prevent="handleSubmit"
                            @submit.native.prevent="handleSubmit"
                        >
                            <ui-dropdown-radio
                                name="eventTypeFilter"
                                :modelValue="filters.event_type"
                                :options="eventTypeOptions"
                                :label="typeFilterLabel"
                                align="left"
                                @update:modelValue="setTypeFilter"
                            />

                            <div class="events-date-filter">
                                <label class="events-date-filter__label">Date From</label>
                                <input type="date" class="events-date-filter__input" :value="filters.date_from" @input="filters.date_from = $event.target.value" />
                            </div>

                            <div class="events-date-filter">
                                <label class="events-date-filter__label">Date To</label>
                                <input type="date" class="events-date-filter__input" :value="filters.date_to" @input="filters.date_to = $event.target.value" />
                            </div>

                            <ui-button
                                :label="filters.include_dismissed ? 'Dismissed Shown' : 'Show Dismissed'"
                                :iconLeft="filters.include_dismissed ? 'fa-eye' : 'fa-eye-slash'"
                                :type="filters.include_dismissed ? 'primary' : 'outline'"
                                @click="setDismissedFilter(filters.include_dismissed ? null : 'true')"
                            />

                            <ui-button
                                label="Submit"
                                type="primary"
                                nativeType="submit"
                            />

                            <ui-button
                                label="Clear"
                                type="primary"
                                :color="hasFilters ? 'red' : null"
                                :disabled="!hasFilters"
                                @click.prevent="clearFilters"
                            />
                        </ui-layout>
                    </ui-layout>

                    <apck-table
                        ref="eventsTable"
                        class="trust-events-table"
                        :columns="cols"
                        :src="tableSrc"
                        :actions="actions"
                        :limit="50"
                        :border="false"
                        :autoload="true"
                        :key="tableKey"
                        :show-toolbar="false"
                        :sglRowAction="viewDetails"
                        newStyleTable
                    />
                </ui-layout>

                <ui-modal
                    v-if="detailsModal.render"
                    title="Event Details"
                    size="lg"
                    align="center"
                    justify="end"
                    fullHeight
                    :open.sync="detailsModal.open"
                    @close="closeDetails()"
                >
                    <template #content>
                        <apck-event-detail-panel
                            v-if="detailsModal.event"
                            :event="detailsModal.event"
                        />
                    </template>
                    <template #footer>
                        <ui-layout gap="2" justify="flex-end">
                            <ui-button
                                v-if="detailsModal.event && !detailsModal.event.dismissed"
                                label="Dismiss"
                                type="outline"
                                @click="dismissEvent"
                            />
                            <ui-button
                                v-if="debugEnabled"
                                label="Re-send Email"
                                type="outline"
                                @click="debugSendEmail"
                            />
                            <ui-button
                                v-if="debugEnabled"
                                label="Re-send Webhook"
                                type="outline"
                                @click="debugSendWebhook"
                            />
                            <ui-button
                                label="Close"
                                type="primary"
                                color="red"
                                @click="closeDetails"
                            />
                        </ui-layout>
                    </template>
                </ui-modal>

                <ui-spinner :show="loading" theme="dark" />
            </template>
        </ui-page>
    `,
};


// -----------------------------------------------------------------------
// Events sidebar component (used on the Dashboard)
// -----------------------------------------------------------------------
export var ApckTrustEventsSidebar = {
    props: {
        dataSrc: {
            type: String,
            default: '/trust/events/list?limit=15',
        },
    },
    data: function () {
        return {
            loading: false,
            events: [],
        };
    },
    mounted: function () {
        this.loadEvents();
    },
    methods: {
        loadEvents: function () {
            var cmp = this;
            cmp.loading = true;
            AjaxUtils.ajax_get({
                url: cmp.dataSrc,
                success: function (resp) {
                    cmp.loading = false;
                    if (resp && resp.success) {
                        cmp.events = resp.data || [];
                    }
                },
                fail: function () {
                    cmp.loading = false;
                },
                opts: { no_response_message: 'Error loading events' }
            });
        },
        dismissEvent: function (ev, e) {
            if (e) e.preventDefault();
            if (e) e.stopPropagation();
            var cmp = this;
            AjaxUtils.ajax_post({
                url: '/trust/events/dismiss',
                data: JSON.stringify({ event_id: ev.id }),
                success: function () {
                    cmp.events = cmp.events.filter(function (item) { return item.id !== ev.id; });
                },
                fail: function () {},
                opts: { no_response_message: 'Error dismissing event' }
            });
        },
        getEventMeta: getEventMeta,
        formatDate: formatDate,
        timeAgo: timeAgo,
        eventUrl: function (ev) {
            // Navigate to the relevant view based on event type
            var d = ev.event_data || {};
            var params = [];
            // Use processing timestamps as date range filter
            if (d.processing_started_at) {
                params.push('date_from=' + encodeURIComponent(d.processing_started_at));
            }
            if (d.processing_completed_at) {
                params.push('date_to=' + encodeURIComponent(d.processing_completed_at));
            }
            var qs;
            switch (ev.event_type) {
                case 'trust_new_vulnerability':
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/vulnerabilities' + qs;
                case 'trust_country_alert':
                    if (d.country_code) params.push('country=' + encodeURIComponent(d.country_code));
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/relationships' + qs;
                case 'trust_risk_alert':
                    if (d.risk_level) params.push('risk_level=' + encodeURIComponent(d.risk_level));
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/relationships' + qs;
                case 'trust_new_geolocation':
                    if (d.country_code) params.push('country=' + encodeURIComponent(d.country_code));
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/countries' + qs;
                case 'trust_geolocation_change':
                case 'trust_new_thirdparty':
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/relationships' + qs;
                case 'trust_domain_expiration':
                case 'trust_recently_registered':
                    qs = params.length ? '?' + params.join('&') : '';
                    return '/trust/relationships' + qs;
                default:
                    return '/trust/events';
            }
        },
        getNotice: function (ev) {
            var d = ev.event_data || {};
            switch (ev.event_type) {
                case 'trust_country_alert':
                    return 'Found ' + (d.relationship_count || 0) + ' trust relationships with ' + (d.country_name || d.country_code || 'flagged country');
                case 'trust_risk_alert':
                    return 'Found ' + (d.relationship_count || 0) + ' ' + (d.risk_name || '') + ' level trust relationships';
                case 'trust_new_vulnerability':
                    return 'Found ' + (d.vulnerability_count || 0) + ' new vulnerabilities in third-party trusts';
                case 'trust_new_thirdparty':
                    return (d.thirdparty_count || 0) + ' new third party relationships detected';
                case 'trust_geolocation_change':
                    return (d.change_count || 0) + ' geolocation changes detected';
                case 'trust_new_geolocation':
                    return 'New geolocation detected: ' + (d.country_code || 'unknown');
                case 'trust_domain_expiration':
                    return (d.thirdparty_count || 0) + ' third parties with expiring domains';
                case 'trust_recently_registered':
                    return (d.thirdparty_count || 0) + ' third parties with recently registered domains';
                default:
                    return ev.event_type.replace(/_/g, ' ');
            }
        },
        getColourClass: function (ev) {
            var meta = getEventMeta(ev.event_type);
            var colour = meta.colour || 'medium';
            // For risk alerts, dynamically pick severity
            if (ev.event_type === 'trust_risk_alert') {
                var level = (ev.event_data || {}).risk_level || 0;
                if (level >= 5) colour = 'critical';
                else if (level >= 4) colour = 'high';
                else colour = 'medium';
            }
            // For new vulns, always critical
            if (ev.event_type === 'trust_new_vulnerability') colour = 'critical';
            return colour;
        },
    },
    template: `
        <div class="trust-events-sidebar">
            <div class="header">
                <h2>Trust Events</h2>
                <a href="/trust/events" class="trust-events-sidebar__viewall">View All</a>
            </div>

            <div v-if="loading" style="position:relative;min-height:60px;">
                <apck-mask :trigger="loading" />
            </div>

            <div v-else-if="!events.length" class="trust-events-sidebar__empty">
                <p>No recent events</p>
            </div>

            <div v-else class="trust-events-sidebar__list">
                <a v-for="ev in events" :key="ev.id" :href="eventUrl(ev)" class="trust-event-entry">
                    <div :class="['trust-event-entry__icon', 'alert-color-' + getColourClass(ev)]">
                        <i :class="getEventMeta(ev.event_type).icon"></i>
                        <div class="alert-icon-bar"></div>
                    </div>

                    <div class="trust-event-entry__body">
                        <div class="trust-event-entry__title">
                            <h3>{{ getEventMeta(ev.event_type).label }}</h3>
                            <div class="event-dismiss" @click.stop.prevent="dismissEvent(ev, $event)">Dismiss</div>
                        </div>
                        <div class="trust-event-entry__notice">{{ getNotice(ev) }}</div>
                        <div class="trust-event-entry__date">
                            <i class="fa-regular fa-clock"></i>
                            <span :title="formatDate(ev.detected_at)">{{ timeAgo(ev.detected_at) }}</span>
                        </div>
                    </div>
                </a>
            </div>
        </div>
    `,
};
